import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { AxiosResponse } from 'axios'
import React, { useEffect, useState } from 'react'
import Spinner from 'src/components/Spinner'
import { useAPIContext } from 'src/contexts/api'
import { useWorkspaceContext } from 'src/contexts/workspace'
import IPipeline, { PipelineStatus, PipelineType } from 'src/models/IPipeline'
import { IIngestionSplitResult, planIngestionSplit, splitIntoIngestion } from 'src/utils/ingestionSplit'

interface IIngestionSplitDialogProps {
    open: boolean
    pipeline: IPipeline
    onClose: () => void
    /** after the split is made, whether or not the new workflow could be published */
    onDone?: (result: IIngestionSplitResult) => void
}

const errorMessage = (e: any): string => e?.response?.data?.message || e?.message || 'Unknown error'

/**
 * MEL-702 (canvas screen 15). Converts a workflow into an ingestion, or fixes a draft ingestion that
 * holds dbt: the pipeline keeps its source, mappers and data store - and with them its run history
 * and sync state - and the other tools it runs move to a new workflow that runs after it.
 *
 * The whole pipeline is read first: the list's copy has no properties, and its components carry no
 * plugin to match actions written with plugin names. The workspace's pipelines are reloaded only
 * when the dialog closes: reloading them unmounts the list rows, and this dialog with them.
 */
const IngestionSplitDialog = (props: IIngestionSplitDialogProps): JSX.Element => {
    const { catalogAPI } = useAPIContext()
    const { workspace, pipelines, fetchPipelines } = useWorkspaceContext()

    const [pipeline, setPipeline] = useState<IPipeline>()
    const [loadError, setLoadError] = useState<string>()
    const [moveDownstream, setMoveDownstream] = useState(true)
    const [isProcessing, setProcessing] = useState(false)
    const [error, setError] = useState<string>()
    const [result, setResult] = useState<IIngestionSplitResult>()

    useEffect(() => {
        if (!props.open)
            return

        catalogAPI.get(props.pipeline._links.self.href)
            .then((r: AxiosResponse<IPipeline>) => setPipeline(r.data))
            .catch((e: any) => setLoadError(errorMessage(e)))
    }, [props.open])

    const listed = props.pipeline
    const label = listed.label || listed.name
    // as the pipeline was when the dialog opened: the row behind it refreshes once the split is made
    const [isConversion] = useState(listed.type !== PipelineType.Ingestion)
    const [isDraft] = useState(listed.status === PipelineStatus.Draft)
    const split = pipeline && planIngestionSplit(pipeline, pipelines || [])
    const canSplit = !!split && !split.blockers.length

    const handleConfirm = async () => {
        if (!pipeline || !split)
            return

        setProcessing(true)
        setError(undefined)

        await splitIntoIngestion(catalogAPI, workspace, pipeline, split, { moveDownstream })
            .then(result => {
                setResult(result)
                props.onDone?.(result)
            })
            .catch((e: Error) => setError(e.message))
            .finally(() => setProcessing(false))
    }

    const handleClose = () => {
        if (result)
            fetchPipelines?.(workspace)

        setError(undefined)
        setResult(undefined)
        props.onClose()
    }

    const names = (components: { name: string }[]) => components.map(dc => dc.name).join(', ')
    const labels = (ps: IPipeline[]) => ps.map(p => `'${p.label || p.name}'`).join(', ')

    const plan = split && <>
        <DialogContentText>
            {`'${label}' will keep ${names(split.keep)}, and its run history and sync state.`}
        </DialogContentText>
        {split.move.length > 0 &&
            <DialogContentText>
                {`${names(split.move)} will move to a new workflow, '${split.workflowLabel}', that runs after each sync.`}
            </DialogContentText>}
        {split.drop.length > 0 &&
            <DialogContentText data-testid='ingestion-split-drop'>
                {`${names(split.drop)} never ran in this pipeline, so it will be removed rather than moved.`}
            </DialogContentText>}
        {split.move.length > 0 && split.downstream.length > 0 &&
            <FormControlLabel
                data-testid='ingestion-split-downstream'
                control={<Checkbox
                    color='primary'
                    checked={moveDownstream}
                    onChange={e => setMoveDownstream(e.target.checked)} />}
                label={`${labels(split.downstream)} ${split.downstream.length === 1 ? 'runs' : 'run'} after '${label}'. `
                    + `Run ${split.downstream.length === 1 ? 'it' : 'them'} after '${split.workflowLabel}' instead, so ${split.downstream.length === 1 ? 'it sees' : 'they see'} what ${names(split.move)} made.`} />}
        {split.move.length > 0 && !pipeline?.actions?.length &&
            <DialogContentText>
                {`If this workspace runs a custom script for it (orchestrate/${split.keep[0]?.name}/elt.sh), the script stops running.`}
            </DialogContentText>}
        {isDraft &&
            <DialogContentText>{`'${label}' stays a draft${split.move.length ? `, and so does '${split.workflowLabel}'` : ''}: save to start running.`}</DialogContentText>}
        {!isDraft && split.move.length > 0 &&
            <DialogContentText>{`A new sync of '${label}' can start while '${split.workflowLabel}' is still running.`}</DialogContentText>}
        {isConversion &&
            <DialogContentText>{"An ingestion can't be turned back into a workflow."}</DialogContentText>}
        {error && <Alert severity='error' data-testid='ingestion-split-error'>{error}</Alert>}
    </>

    const done = result && <>
        <Alert severity='success'>
            {`'${label}' is now an ingestion${result.workflow ? `, and '${result.workflow.label}' runs after it` : ''}.`}
        </Alert>
        {result.workflowError &&
            <Alert severity='warning' data-testid='ingestion-split-workflow-error'>
                {`'${result.workflow?.label}' was saved as a draft and won't run until you fix it: ${result.workflowError}`}
            </Alert>}
        {result.downstreamErrors.length > 0 &&
            <Alert severity='warning' data-testid='ingestion-split-downstream-error'>
                {`These still run after '${label}', not '${result.workflow?.label}': ${result.downstreamErrors.join('; ')}`}
            </Alert>}
    </>

    return (
        <Dialog open={props.open} onClose={handleClose} aria-labelledby='ingestion-split-title'>
            <DialogTitle id='ingestion-split-title'>
                {isConversion ? `Convert '${label}' to an ingestion?` : `Move tools out of '${label}'?`}
            </DialogTitle>
            <DialogContent>
                {loadError
                    ? <Alert severity='error'>{loadError}</Alert>
                    : !split
                        ? <CircularProgress size='1.5em' />
                        : split.blockers.length
                            ? <>
                                <DialogContentText>{`'${label}' can't become an ingestion yet:`}</DialogContentText>
                                <ul data-testid='ingestion-split-blockers'>
                                    {split.blockers.map(b => <li key={b}>{b}</li>)}
                                </ul>
                                <DialogContentText>Edit the pipeline to fix this, then try again.</DialogContentText>
                            </>
                            : result ? done : plan}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isProcessing}>
                    {result || !canSplit ? 'Close' : 'Cancel'}
                </Button>
                {!result && canSplit &&
                    <Button variant='contained' color='primary' disabled={isProcessing} onClick={handleConfirm}>
                        <Spinner active={isProcessing}>
                            {isConversion ? 'Convert' : 'Move'}
                        </Spinner>
                    </Button>}
            </DialogActions>
        </Dialog>
    )
}

export default IngestionSplitDialog
